import React, { useCallback, useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Upload, FileSpreadsheet, Users, Store, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { authFetch, getApiBase } from '../lib/api'
import { useLoadingBar } from '../contexts/LoadingBarContext'
import { ConfirmModal } from './shared/ConfirmModal'

interface AllowedCompetitor {
  id: number
  name: string
  created_at: string
}

export function CompetitorUpload() {
  const [isDragging, setIsDragging] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [status, setStatus] = useState<'idle' | 'uploading' | 'error'>('idle')
  const [competitors, setCompetitors] = useState<AllowedCompetitor[]>([])
  const [loading, setLoading] = useState(true)
  const [isClearModalOpen, setIsClearModalOpen] = useState(false)
  const [clearLoading, setClearLoading] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AllowedCompetitor | null>(
    null
  )
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const uploadDialogRef = useRef<HTMLDialogElement>(null)
  const loadingBar = useLoadingBar()

  const fetchCompetitors = useCallback(async () => {
    setLoading(true)
    loadingBar.start()
    try {
      const res = await authFetch(`${getApiBase()}/api/competitors`)
      if (res.ok) {
        const data = await res.json()
        setCompetitors(data)
      }
    } catch {
      toast.error('Failed to load competitors')
    } finally {
      setLoading(false)
      loadingBar.done()
    }
  }, [loadingBar])

  useEffect(() => {
    fetchCompetitors()
  }, [fetchCompetitors])

  const closeUploadModal = useCallback(() => {
    if (status !== 'uploading') {
      uploadDialogRef.current?.close()
      setIsUploadModalOpen(false)
      setFile(null)
      setStatus('idle')
    }
  }, [status])

  useEffect(() => {
    if (isUploadModalOpen) uploadDialogRef.current?.showModal()
  }, [isUploadModalOpen])

  useEffect(() => {
    const dialog = uploadDialogRef.current
    if (!dialog || !isUploadModalOpen) return

    const handleBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        closeUploadModal()
      }
    }

    dialog.addEventListener('mousedown', handleBackdropClick)
    return () => dialog.removeEventListener('mousedown', handleBackdropClick)
  }, [isUploadModalOpen, closeUploadModal])

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true)
    } else if (e.type === 'dragleave') {
      setIsDragging(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (e.dataTransfer.files?.[0]) {
      validateAndSetFile(e.dataTransfer.files[0])
    }
  }, [])

  const validateAndSetFile = (f: File) => {
    if (
      f.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      f.name.endsWith('.xlsx')
    ) {
      setFile(f)
      setStatus('idle')
    } else {
      setStatus('error')
      toast.error('Please upload a valid Excel (.xlsx) file')
    }
  }

  const handleUpload = async () => {
    if (!file) return
    setStatus('uploading')
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await authFetch(`${getApiBase()}/api/competitors/upload`, {
        method: 'POST',
        body: formData,
      })
      const data = await res.json()
      if (res.ok) {
        toast.success(
          `Imported ${data.imported ?? 0} competitors` +
            (data.skipped > 0 ? ` (${data.skipped} already existed)` : '')
        )
        setFile(null)
        setStatus('idle')
        setIsUploadModalOpen(false)
        fetchCompetitors()
      } else {
        toast.error(data.error || 'Upload failed')
        setStatus('error')
      }
    } catch {
      toast.error('Upload failed')
      setStatus('error')
    } finally {
      setStatus('idle')
    }
  }

  const handleClearClick = () => {
    setIsClearModalOpen(true)
  }

  const handleClearConfirm = async () => {
    setClearLoading(true)
    try {
      const res = await authFetch(`${getApiBase()}/api/competitors`, {
        method: 'DELETE',
      })
      if (res.ok) {
        toast.success('Competitors cleared')
        fetchCompetitors()
        setIsClearModalOpen(false)
      } else {
        toast.error('Failed to clear')
      }
    } catch {
      toast.error('Failed to clear')
    } finally {
      setClearLoading(false)
    }
  }

  const handleDeleteClick = (c: AllowedCompetitor) => {
    setDeleteTarget(c)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleteLoading(true)
    try {
      const res = await authFetch(
        `${getApiBase()}/api/competitors/${deleteTarget.id}`,
        { method: 'DELETE' }
      )
      if (res.ok) {
        toast.success(`"${deleteTarget.name}" removed`)
        fetchCompetitors()
        setDeleteTarget(null)
      } else {
        const data = await res.json().catch(() => ({}))
        toast.error(data.error || 'Failed to delete')
      }
    } catch {
      toast.error('Failed to delete')
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <div className='space-y-6'>
      <div className='rounded-2xl border border-border bg-card shadow-lg shadow-black/5 p-6'>
        <h3 className='text-lg font-semibold mb-2 text-foreground'>
          Allowed Competitors
        </h3>
        <p className='text-sm text-muted-foreground mb-4'>
          Upload an Excel file with a column named exactly
          &quot;Competitors&quot;. Only competitors in this list will appear in
          the Competitors field of scraped results.
        </p>
        <button
          type='button'
          onClick={() => setIsUploadModalOpen(true)}
          className='inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg hover:from-blue-500 hover:to-indigo-500 transition-all'
        >
          <Upload size={18} />
          Upload Excel
        </button>
      </div>

      {isUploadModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <dialog
            ref={uploadDialogRef}
            onClose={closeUploadModal}
            onCancel={e => status === 'uploading' && e.preventDefault()}
            className='fixed top-1/2 left-1/2 z-[100] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden [&::backdrop]:bg-black/40 [&::backdrop]:backdrop-blur-[2px]'
            aria-labelledby='upload-modal-title'
          >
            <div className='relative w-full rounded-2xl bg-white overflow-hidden flex flex-col max-h-[85vh]'>
              <div className='flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200 shrink-0'>
                <h2
                  id='upload-modal-title'
                  className='text-base sm:text-lg font-semibold text-slate-900'
                >
                  Upload Excel
                </h2>
                <button
                  type='button'
                  onClick={closeUploadModal}
                  disabled={status === 'uploading'}
                  className='p-1.5 sm:p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50'
                  aria-label='Close'
                >
                  <X size={18} />
                </button>
              </div>
              <div className='px-4 py-4 sm:px-6 sm:py-6 overflow-auto'>
                <p className='text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4'>
                  Column must be named exactly &quot;Competitors&quot;.
                  Supported: .xlsx
                </p>
                <div
                  className={`
                  relative border-2 border-dashed rounded-xl p-5 sm:p-8 text-center transition-all duration-200
                  ${isDragging ? 'border-primary bg-primary/10 scale-[1.01] shadow-inner' : 'border-muted-foreground/30 hover:border-primary/40 hover:bg-muted/30'}
                `}
                >
                  <input
                    type='file'
                    accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onChange={e =>
                      e.target.files?.[0] &&
                      validateAndSetFile(e.target.files[0])
                    }
                    className='absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10'
                    id='competitor-file-modal'
                  />
                  <label
                    htmlFor='competitor-file-modal'
                    className='flex flex-col items-center gap-1.5 sm:gap-2'
                  >
                    <FileSpreadsheet className='h-10 w-10 sm:h-12 sm:w-12 text-primary' />
                    {file ? (
                      <span className='text-xs sm:text-sm font-medium break-all px-1'>
                        {file.name}
                      </span>
                    ) : (
                      <span className='text-xs sm:text-sm text-muted-foreground'>
                        Drag & drop or click to select Excel file
                      </span>
                    )}
                  </label>
                  {file && (
                    <div className='mt-3 sm:mt-5 flex gap-3 justify-center relative z-20'>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation()
                          handleUpload()
                        }}
                        disabled={status === 'uploading'}
                        className='px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-xs sm:text-sm font-semibold disabled:opacity-50 flex items-center gap-2 shadow-md hover:shadow-lg hover:from-blue-500 hover:to-indigo-500 transition-all'
                      >
                        <Upload size={16} />
                        {status === 'uploading' ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </dialog>,
          document.body
        )}

      <div className='rounded-2xl border border-border bg-card shadow-lg shadow-black/5 p-6'>
        <div className='flex flex-wrap items-center justify-between gap-3 mb-5'>
          <h3 className='text-lg font-semibold flex items-center gap-2 text-foreground'>
            <div className='p-1.5 rounded-lg bg-primary/10'>
              <Users size={18} className='text-primary' />
            </div>
            Current List ({competitors.length})
          </h3>
          {competitors.length > 0 && (
            <button
              type='button'
              onClick={handleClearClick}
              className='px-5 py-2.5 bg-red-50 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-100 border border-red-200 transition-all'
            >
              Clear All
            </button>
          )}
        </div>
        {(() => {
          if (loading) {
            return (
              <div className='py-12 text-center'>
                <div className='inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent' />
                <p className='text-sm text-muted-foreground mt-3'>Loading...</p>
              </div>
            )
          }
          if (competitors.length === 0) {
            return (
              <div className='py-12 px-4 rounded-xl bg-muted/30 border border-dashed border-border text-center'>
                <Store
                  size={32}
                  className='mx-auto text-muted-foreground/50 mb-2'
                />
                <p className='text-sm text-muted-foreground'>
                  No competitors yet. Click &quot;Upload Excel&quot; above to
                  add your allowed list.
                </p>
              </div>
            )
          }
          return (
            <div className='max-h-80 overflow-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
              {competitors.map((c, idx) => (
                <div
                  key={c.id}
                  className='flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100/80 border border-slate-200/80 shadow-sm hover:shadow-md hover:border-primary/30 hover:from-blue-50/50 hover:to-indigo-50/30 transition-all duration-200 group'
                >
                  <div className='flex-shrink-0 h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary/20 transition-colors'>
                    <Store size={14} />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <span className='text-sm font-medium text-foreground truncate block'>
                      {c.name}
                    </span>
                    <span className='text-[10px] text-muted-foreground'>
                      #{idx + 1}
                    </span>
                  </div>
                  <button
                    type='button'
                    onClick={() => handleDeleteClick(c)}
                    className='flex-shrink-0 p-1.5 rounded-lg text-red-600 hover:bg-red-100 border border-transparent hover:border-red-200 transition-colors'
                    title='Delete'
                    aria-label={`Delete ${c.name}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )
        })()}
      </div>

      <ConfirmModal
        isOpen={isClearModalOpen}
        title='Clear all competitors?'
        description='This will remove your entire allowed competitors list. Scraped data will show no competitors until you upload a new list.'
        confirmText='Yes, clear all'
        cancelText='Cancel'
        variant='danger'
        loading={clearLoading}
        onClose={() => {
          if (!clearLoading) setIsClearModalOpen(false)
        }}
        onConfirm={handleClearConfirm}
      />
      <ConfirmModal
        isOpen={deleteTarget !== null}
        title='Remove this competitor?'
        description={
          deleteTarget
            ? `"${deleteTarget.name}" will be removed from your allowed list.`
            : ''
        }
        confirmText='Yes, remove'
        cancelText='Cancel'
        variant='danger'
        loading={deleteLoading}
        onClose={() => {
          if (!deleteLoading) setDeleteTarget(null)
        }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
